import { useState } from "react";
import type { InventionElementResponse } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { ElementLadder, type LadderWrites } from "./ElementLadder";
import type { ElementCardProps, Openable } from "./ElementCard";

/**
 * The question asked before an element goes.
 *
 * It names what is being removed, so the attorney reads what they are dropping
 * rather than agreeing to a generic prompt.
 */
function RemoveConfirm({
  text,
  only,
  busy,
  onRemove,
  onKeep,
}: {
  text: string;
  /** Whether this is the last element, so removing it empties the invention. */
  only: boolean;
  busy: boolean;
  onRemove: () => void;
  onKeep: () => void;
}) {
  return (
    <div className="mt-2.5 border-t border-dashed border-border pt-2.5">
      <p className="text-[12px]">
        Remove &ldquo;{text}&rdquo;? This cannot be undone.
      </p>
      {only && (
        <p className="mt-1 text-[12px] text-warning-strong">
          This is the only element. Removing it returns the page to its empty
          state.
        </p>
      )}
      <div className="mt-2 flex gap-2">
        <Button
          type="button"
          variant="secondary"
          size="dense"
          onClick={onRemove}
          disabled={busy}
        >
          Remove element
        </Button>
        <Button type="button" variant="secondary" size="dense" onClick={onKeep}>
          Keep it
        </Button>
      </div>
    </div>
  );
}

/**
 * What is open on an element.
 *
 * Only one awaiting attention is offered confirmation: an element grounded in
 * the disclosure is never presented for it.
 */
function ElementActions({
  awaiting,
  busy,
  amendLocked,
  onAddFallback,
  onConfirm,
  onAmend,
  onRemove,
}: {
  awaiting: boolean;
  busy: boolean;
  /** For an element with no fallbacks: the way to its first, at the row's start. */
  onAddFallback?: () => void;
  /** Another element's editor is open; only one may be at a time. */
  amendLocked: boolean;
  onConfirm: () => void;
  onAmend: () => void;
  onRemove: () => void;
}) {
  return (
    // At the right, as the design sets a card's acts, with the act that
    // settles the element last.
    <div className="mt-2.5 flex justify-end gap-[7px] border-t border-dashed border-border pt-[9px]">
      {onAddFallback !== undefined && (
        <Button
          type="button"
          variant="quiet"
          size="dense"
          className="mr-auto"
          onClick={onAddFallback}
        >
          Add fallback
        </Button>
      )}
      <Button
        type="button"
        variant="danger-outline"
        size="dense"
        onClick={onRemove}
      >
        Remove
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="dense"
        onClick={onAmend}
        disabled={amendLocked}
      >
        Amend
      </Button>
      {awaiting && (
        <Button
          type="button"
          variant="confirm"
          size="dense"
          onClick={onConfirm}
          disabled={busy}
        >
          Confirm
        </Button>
      )}
    </div>
  );
}

/** Under the provenance: why the element was flagged, if it was, and the ladder beneath it. */
function Beneath({
  element,
  busy,
  ladder,
  openable,
  onOpenSource,
  addingFirst,
  onAddingFirstDone,
}: {
  element: InventionElementResponse;
  busy: boolean;
  ladder: LadderWrites;
  openable: Openable;
  onOpenSource: (fileId: string) => void;
  addingFirst: boolean;
  onAddingFirstDone: () => void;
}) {
  return (
    <>
      {element.flag_reason !== null && (
        <p className="mt-1.5 text-[11px] leading-[1.45] text-muted-foreground">
          {element.flag_reason}
        </p>
      )}
      <ElementLadder
        element={element}
        busy={busy}
        writes={ladder}
        openable={openable}
        onOpenSource={onOpenSource}
        addingFirst={addingFirst}
        onAddingFirstDone={onAddingFirstDone}
      />
    </>
  );
}

/**
 * The foot of an element's card: the ladder, the question before a removal,
 * and the acts. Which editor is open is the panel's to know, since only one
 * may be; the removal question and the first fallback's form are the card's.
 */
export function CardFoot({
  awaiting,
  ...props
}: ElementCardProps & { awaiting: boolean }) {
  const { element, busy, amending, amendLocked, only, ladder } = props;
  const [removing, setRemoving] = useState(false);
  const [addingFirst, setAddingFirst] = useState(false);
  const bare = element.fallbacks.length === 0;
  return (
    <>
      <Beneath
        element={element}
        busy={busy}
        ladder={ladder}
        openable={props.openable}
        onOpenSource={props.onOpenSource}
        addingFirst={addingFirst && bare}
        onAddingFirstDone={() => {
          setAddingFirst(false);
        }}
      />
      {removing && (
        <RemoveConfirm
          text={element.text}
          only={only}
          busy={busy}
          onRemove={props.onRemove}
          onKeep={() => {
            setRemoving(false);
          }}
        />
      )}
      {!removing && !amending && (
        <ElementActions
          awaiting={awaiting}
          busy={busy}
          amendLocked={amendLocked}
          onAddFallback={
            bare && !addingFirst
              ? () => {
                  setAddingFirst(true);
                }
              : undefined
          }
          onConfirm={props.onConfirm}
          onAmend={props.onAmendOpen}
          onRemove={() => {
            setRemoving(true);
          }}
        />
      )}
    </>
  );
}
